import React, { useState, useEffect, useContext, useRef } from "react"
import DataContext, { defaultState } from "./DataContext"
import {
  getUserGroups,
  getRegionsListing,
  getTagsProduct,
  getTagCategoryProduct,
  getRetailStores,
  getMyAssets,
  getMyProducts,
  getTagsListing,
  getTagCategory,
  // getSubUserListing,
  getPageNumbers,
  getMyBookmarks,
  getMyListing,
  getOnSiteMarketing,
  getMyNotifications,
  // getMyMessageSent,
  // getMyMessageReceived,
  clearMyNotification,
  clearMyNotifications,
  // getStoreCompanies,
  // getStoreRegions,
  // getStoreCities,
  getMyActiveMessages,
} from "../../helpers/DrupalHelper"
import StateContext from "../state/StateContext"
import AuthenticationContext from "../authentication/AuthenticationContext"

// The company-groups endpoint answers either as a bare array or wrapped in
// {data: [...]}, so both shapes have to be accepted before anything can tell a
// usable response from a failed one.
const toUserGroupArray = res =>
  Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : null

// Builds the user-group slice of provider state from the two group requests.
// Extracted so the early publisher can apply it without waiting for the rest
// of the startup batch (#329).
const extractUserGroupUpdates = (userGroupsResult, myListingResult) => {
  const updates = {}

  if (userGroupsResult.status === "fulfilled" && userGroupsResult.value) {
    updates.universalUserGroups = userGroupsResult.value.default
  }

  if (myListingResult.status === "fulfilled" && myListingResult.value) {
    const dataArray = toUserGroupArray(myListingResult.value)
    if (dataArray) {
      const selectData = dataArray.map(v => ({
        value: v.id,
        label: v.title,
      }))
      updates.myUserGroups = dataArray
      updates.userGroups = dataArray
      updates.myUsersAsOptions = selectData
      updates.myUserGroupsAsOptions = selectData
    }
  }

  return updates
}

// Reports a group source that could not be loaded. Neither call reliably
// rejects — getMyListing resolves with {error: true} on a network or HTTP
// failure, and getUserGroups never checks response.ok — so a rejected-only
// check would miss both. Without this, a total group-load failure is silent on
// an account whose assets carry no allocation segments, because the
// unresolved-segment warning in InventoryProvider never gets a chance to fire.
const logGroupLoadFailures = (userGroupsResult, myListingResult, companyId) => {
  // With no company there is no universal-groups request to judge; the
  // resolved null is expected, not a failure.
  if (companyId && !Array.isArray(userGroupsResult.value?.default)) {
    console.error(
      "Universal user groups failed to load — allocations may render as unallocated",
      userGroupsResult.status === "rejected"
        ? userGroupsResult.reason
        : userGroupsResult.value
    )
  }

  const myListingValue =
    myListingResult.status === "fulfilled" ? myListingResult.value : null
  if (!toUserGroupArray(myListingValue)) {
    console.error(
      "Company user groups failed to load — allocations may render as unallocated",
      myListingResult.status === "rejected"
        ? myListingResult.reason
        : myListingValue
    )
  }
}

export const DataProvider = props => {
  const { state } = useContext(StateContext)
  const { authentication } = useContext(AuthenticationContext)

  const { token } = state
  const { currentUserData } = authentication

  const [data, setData] = useState(defaultState)

  // Identifies the newest run() so a slower earlier one cannot publish the
  // previous session's groups after it (#329).
  const latestRunIdRef = useRef(0)

  const updateState = (key, value) => {
    let selectData = false
    switch (key) {
      case "userGroups":
        selectData = value.map(v => {
          return {
            value: v.id,
            label: v.title,
          }
        })
        break
      default:
        selectData = value.map(v => {
          return {
            value: v.id,
            label: v.title,
          }
        })
        break
    }

    const obj = {
      [key]: value,
    }

    if (selectData) {
      obj[`${key}AsOptions`] = selectData
    }

    setData(prevState => ({
      ...prevState,
      ...obj,
    }))
  }

  useEffect(() => {
    if (!currentUserData.isAuthenticated) return

    const myBrandsAsOptions = (currentUserData.brands || []).map(i => ({
      value: i.id,
      label: i.title,
      img: i.logo,
      company_id: i.company_id,
    }))

    const updates = {
      myBrands: currentUserData.brands,
      myBrandsAsOptions: myBrandsAsOptions,
    }

    // Set companies immediately so InventorySubmission and User Segmentation
    // don't wait for other API calls.
    if (currentUserData.companies?.length) {
      updates.currentUserCompaniesAsOp = currentUserData.companies.map(v => ({
        value: v.id,
        label: v.title,
      }))
    }

    setData(prevState => ({
      ...prevState,
      ...updates,
    }))
  }, [
    currentUserData.isAuthenticated,
    currentUserData.brands,
    currentUserData.companies,
  ])
  const run = async () => {
    if (!currentUserData.isAuthenticated) return

    const runId = ++latestRunIdRef.current

    try {
      // Prepare company ID if available.
      const companyId = currentUserData.companies?.[0]?.id

      // The allocation Internal/Retail split cannot be computed until these
      // two land, so they are published the moment they resolve rather than
      // waiting for the slowest request in the batch below (#329).
      const userGroupsPromise = companyId
        ? getUserGroups(token, companyId)
        : Promise.resolve(null)
      const myUserGroupsPromise = getMyListing(
        token,
        "user_group",
        "default",
        "all"
      )

      Promise.allSettled([userGroupsPromise, myUserGroupsPromise]).then(
        ([userGroupsResult, myListingResult]) => {
          // A newer run() has superseded this one; its groups are the
          // authoritative ones.
          if (runId !== latestRunIdRef.current) return

          logGroupLoadFailures(userGroupsResult, myListingResult, companyId)

          setData(prevState => ({
            ...prevState,
            ...extractUserGroupUpdates(userGroupsResult, myListingResult),
            // Set even when both requests failed, so consumers can tell
            // "finished, nothing to show" from "still loading".
            userGroupsLoaded: true,
          }))
        }
      )

      // Execute all independent API calls in parallel using Promise.allSettled.
      // This allows graceful degradation - if some APIs fail, others still succeed.
      const results = await Promise.allSettled([
        // User and groups. Already published above; kept in the batch so the
        // positional destructuring below stays unchanged.
        userGroupsPromise,
        myUserGroupsPromise,

        // Counts and notifications.
        getPageNumbers(token),
        getMyNotifications(token),
        getMyBookmarks(token),

        // Geography and stores.
        getRegionsListing(token),
        getRetailStores(token),

        // Products (assets loaded lazily by My Assets page).
        getMyProducts(token),

        // Tags and categories.
        getTagsListing(token, "default"),
        getTagCategory(token),
        getTagsProduct(token),
        getTagCategoryProduct(token),

        // Marketing and messages.
        getOnSiteMarketing(token),
        getMyActiveMessages(token, {}),
      ])

      // Extract results with proper error handling.
      const [
        userGroupsResult,
        myListingResult,
        pageNumbersResult,
        notificationsResult,
        bookmarksResult,
        regionsResult,
        retailStoresResult,
        productsResult,
        tagsListingResult,
        tagCategoryResult,
        tagsProductResult,
        tagCategoryProductResult,
        onSiteMarketingResult,
        activeMessagesResult,
      ] = results

      // Build state updates object - single update at the end prevents multiple re-renders.
      const stateUpdates = {}

      // The two group results are handled by the early publisher above and are
      // deliberately absent from stateUpdates — the final setData spreads over
      // prevState, so the values it already wrote are preserved.

      // Process page numbers result.
      if (pageNumbersResult.status === "fulfilled" && pageNumbersResult.value) {
        const res = pageNumbersResult.value
        stateUpdates.entityItemsNumber = res
        stateUpdates.myBookmarksItems = res.bookmarked
      }

      // Process notifications result.
      if (
        notificationsResult.status === "fulfilled" &&
        notificationsResult.value
      ) {
        stateUpdates.myNotifications = notificationsResult.value
      }

      // Process bookmarks result.
      if (bookmarksResult.status === "fulfilled" && bookmarksResult.value) {
        const res = bookmarksResult.value
        const product = res.map(item => item["product"]?.id).filter(Boolean)
        const asset = res.map(item => item["asset"]?.id).filter(Boolean)
        const retailer = res
          .map(item =>
            item["retailer"] && Array.isArray(item["retailer"])
              ? item["retailer"][0]?.id
              : item["retailer"]?.id
          )
          .filter(Boolean)
        const store = res.map(item => item["store"]?.id).filter(Boolean)
        const brand = res.map(item => item["brand"]?.id).filter(Boolean)

        stateUpdates.myBookmarks = res
        stateUpdates.myBookmarksID = { product, store, asset, brand, retailer }
      }

      // Process regions result.
      if (regionsResult.status === "fulfilled" && regionsResult.value) {
        const res = regionsResult.value
        let regionsAsOptions = []
        let regionsAsOptionsCA = []
        let regionsAsOptionsUS = []

        res["CA"]?.map((i, k) => {
          const pushCa = {
            value: i.id,
            label: i.name + ` (${i.code}, CA)`,
            region_code: i.code,
          }
          regionsAsOptions.push(pushCa)
          regionsAsOptionsCA.push(pushCa)
        })

        res["US"]?.map((i, k) => {
          const pushUs = {
            value: i.id,
            label: i.name + ` (${i.code}, US)`,
            region_code: i.code,
          }
          regionsAsOptions.push(pushUs)
          regionsAsOptionsUS.push(pushUs)
        })

        stateUpdates.regions = res
        stateUpdates.regionsAsOptions = regionsAsOptions
        stateUpdates.regionsAsOptionsUS = regionsAsOptionsUS
        stateUpdates.regionsAsOptionsCA = regionsAsOptionsCA
      }

      // Process retail stores result.
      if (
        retailStoresResult.status === "fulfilled" &&
        retailStoresResult.value
      ) {
        const res = retailStoresResult.value
        const retailStoresAsOptions = res.map((i, k) => ({
          value: i.id,
          label: i.name,
        }))
        stateUpdates.retailStores = res
        stateUpdates.retailStoresAsOptions = retailStoresAsOptions
      }

      // Process products result (assets are loaded lazily by My Assets page).
      if (productsResult.status === "fulfilled" && productsResult.value) {
        const res = productsResult.value
        const myProductsAsOptions = res.map((i, k) => ({
          value: i.id,
          label: i.title,
        }))
        stateUpdates.myProducts = res
        stateUpdates.myProductsAsOptions = myProductsAsOptions
      }

      // Process tags listing result.
      if (tagsListingResult.status === "fulfilled" && tagsListingResult.value) {
        const res = tagsListingResult.value
        const tagsAsOptions = res.map((i, k) => ({
          value: i.id,
          label: i.title,
        }))
        stateUpdates.tagsAsOptions = tagsAsOptions
      }

      // Process tag category result.
      if (tagCategoryResult.status === "fulfilled" && tagCategoryResult.value) {
        const res = tagCategoryResult.value
        const categoriesAsOptions = res.map((i, k) => ({
          value: i.id,
          label: i.title,
        }))
        stateUpdates.categoriesAsOptions = categoriesAsOptions
      }

      // Process tags product result.
      if (tagsProductResult.status === "fulfilled" && tagsProductResult.value) {
        const res = tagsProductResult.value
        const productTagsAsOptions = res.map((i, k) => ({
          value: i.id,
          label: i.title,
        }))
        stateUpdates.productTagsAsOptions = productTagsAsOptions
      }

      // Process tag category product result.
      if (
        tagCategoryProductResult.status === "fulfilled" &&
        tagCategoryProductResult.value
      ) {
        const res = tagCategoryProductResult.value
        const productCategoriesAsOptions = res.map((i, k) => ({
          value: i.id,
          label: i.title,
        }))
        stateUpdates.productCategoriesAsOptions = productCategoriesAsOptions
      }

      // Process on-site marketing result.
      if (
        onSiteMarketingResult.status === "fulfilled" &&
        onSiteMarketingResult.value
      ) {
        stateUpdates.onSiteMarketing = onSiteMarketingResult.value
      }

      // Process active messages result.
      if (
        activeMessagesResult.status === "fulfilled" &&
        activeMessagesResult.value
      ) {
        stateUpdates.messages = {
          ...data.messages,
          sentMessages: activeMessagesResult.value,
        }
      }

      // Handle company-specific data.
      if (currentUserData.companies?.length) {
        const currentUserCompanies = currentUserData.companies.map(v => ({
          value: v.id,
          label: v.title,
        }))
        stateUpdates.currentUserCompaniesAsOp = currentUserCompanies
      }

      // Single state update with all data - prevents multiple re-renders.
      setData(prevState => ({
        ...prevState,
        ...stateUpdates,
        // Mark loading as complete.
        isLoading: false,
        loadingStates: {
          userGroups: false,
          assets: false,
          products: false,
          notifications: false,
          bookmarks: false,
          regions: false,
          stores: false,
          tags: false,
          categories: false,
          marketing: false,
          messages: false,
        },
      }))

      // Log any failed requests for debugging.
      results.forEach((result, index) => {
        if (result.status === "rejected") {
          console.error(`API call ${index} failed:`, result.reason)
        }
      })
    } catch (error) {
      console.error("Error loading data:", error)
      // Mark loading as complete even on error.
      setData(prevState => ({
        ...prevState,
        isLoading: false,
      }))
    }
  }

  useEffect(() => {
    if (!currentUserData.isAuthenticated) {
      // Sign-out: drop the previous session's groups so the next account can
      // never be classified against them, and mark them un-loaded again. The
      // ref bump also invalidates any group request still in flight (#329).
      latestRunIdRef.current += 1
      setData(prevState =>
        prevState.userGroupsLoaded
          ? {
              ...prevState,
              userGroupsLoaded: false,
              universalUserGroups: [],
              myUserGroups: [],
            }
          : prevState
      )
      return
    }
    run()
  }, [currentUserData.isAuthenticated, token])

  const myNotificationsClearAll = async () => {
    await clearMyNotifications(token).then(res =>
      getMyNotifications(token).then(res => {
        setData(prevState => ({
          ...prevState,
          myNotifications: res,
        }))
      })
    )
  }
  const myNotificationsClearSingle = async id => {
    await clearMyNotification(token, id).then(res =>
      getMyNotifications(token).then(res => {
        setData(prevState => ({
          ...prevState,
          myNotifications: res,
        }))
      })
    )
  }

  return (
    <DataContext.Provider
      value={{
        updateState: updateState,
        data: data,
        setData: setData,
        myNotificationsClearAll: myNotificationsClearAll,
        myNotificationsClearSingle,
        myNotificationsClearSingle,
      }}
    >
      {props.children}
    </DataContext.Provider>
  )
}

export default DataProvider
